/*
 * <custom-size-input> — shows the measurement fields rendered by snippets/custom-size-input.liquid
 * only while the shopper has the "Custom Size" option selected.
 *
 * The fields live outside the product form and are wired to it with a `form` attribute, so the
 * only thing to manage is whether they submit: disabled inputs are left out of the form data,
 * which keeps a non custom variant from carrying empty measurement properties to the cart.
 * Values are kept when hiding so toggling back to Custom Size does not lose what was typed.
 *
 * Liquid renders the correct initial state, so the element only reacts to variant changes.
 *
 * A field set may also ask different measurements per body type — the pants form asks men for a
 * waist and women for a waist type plus the measurement. Those fields are wrapped in a
 * `.custom-size__group[data-body-type]`, and only the group matching the "Ordering For" select is
 * shown and enabled; the rest stay disabled so the cart only ever gets the answered questions.
 */

class CustomSizeInput extends HTMLElement {
  connectedCallback() {
    this.fields = Array.from(this.querySelectorAll('.custom-size__input'));
    this.bodyTypeGroups = Array.from(this.querySelectorAll('.custom-size__group[data-body-type]'));
    // Rendered by custom-size-field with label 'Ordering For', which handleizes to this id.
    this.bodyTypeSelect = this.querySelector('#custom-size-ordering-for');

    if (this.bodyTypeGroups.length && this.bodyTypeSelect) {
      this.bodyTypeSelect.addEventListener('change', () => this.setActive(!this.hidden));
    }

    this.setActive(!this.hidden);

    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      this.setActive(this.isCustomSize(event?.data?.variant));
    });
  }

  disconnectedCallback() {
    this.variantChangeUnsubscriber?.();
  }

  isCustomSize(variant) {
    if (!variant?.options) return false;

    return variant.options.some((option) => String(option).trim() === this.dataset.optionValue);
  }

  setActive(active) {
    this.hidden = !active;

    const bodyType = this.bodyTypeSelect?.value.trim();
    this.bodyTypeGroups.forEach((group) => {
      group.hidden = group.dataset.bodyType !== bodyType;
    });

    this.fields.forEach((field) => {
      const group = field.closest('.custom-size__group[data-body-type]');
      field.disabled = !active || (group != null && group.hidden);
    });
  }
}

if (!customElements.get('custom-size-input')) {
  customElements.define('custom-size-input', CustomSizeInput);
}
